import { Dispatch } from "react";

import { getUserDropAction } from "@/contexts/DndKitContextDefaults/utils/getUserDropAction/getUserDropAction";
import { captureAppWarning } from "@/utils/monitoring/sentry";

import { CustomCellData } from "@/pages/EditSubjectPlan/types/types";

import {
  Action,
  BatchUpdateTableStructuresPayload,
  TableData,
} from "@/states/unitTableReducer/unitTableReducer";

import type { DragEndEvent } from "@dnd-kit/core/dist/types/events";

// This is specifically to move the logic of the event handlers out of the component
export const useHandleTableEvents = ({
  dispatch,
  state,
}: {
  dispatch: Dispatch<Action>;
  state: TableData;
}) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { activatorEvent, active, over } = event;
    const activeData = active.data.current?.data;
    const overData = over?.data.current?.data;

    switch (getUserDropAction(event)) {
      case "USER_REMOVES_UNIT": {
        dispatch({
          type: "REMOVE_CELL",
          payload: {
            cellId: activeData.cellId,
          },
        });
        break;
      }

      case "USER_ADDS_UNIT": {
        dispatch({
          type: "ADD_CELL",
          payload: {
            resourceId: activeData.resourceId,
            row: overData.row,
            column: overData.column,
          },
        });
        break;
      }

      case "USER_MOVES_UNIT": {
        dispatch({
          type: "MOVE_CELL",
          payload: {
            cellId: activeData.cellId,
            row: overData.row,
            column: overData.column,
          },
        });
        break;
      }

      case "USER_EXTENDS_UNIT_RIGHT": {
        const indexDiffRight = overData.cellIndex - activeData.cellIndex;
        const currentSpan =
          state.cells.find((cell) => cell.id === activeData.cellId)?.span ?? 1;
        const spanRight = currentSpan + (indexDiffRight - (currentSpan - 1));

        dispatch({
          type: "CHANGE_CELL_SPAN_FROM_RIGHT",
          payload: {
            cellId: activeData.cellId,
            span: spanRight,
          },
        });
        break;
      }

      case "USER_EXTENDS_UNIT_LEFT": {
        const indexDiffLeft = activeData.cellIndex - overData.cellIndex;
        const spanLeft =
          (state.cells.find((cell) => cell.id === activeData.cellId)?.span ??
            1) + indexDiffLeft;

        dispatch({
          type: "CHANGE_CELL_SPAN_FROM_LEFT",
          payload: {
            cellId: activeData.cellId,
            span: spanLeft,
            column: overData.column,
          },
        });
        break;
      }

      case "USER_CONTRACTS_UNIT_FROM_RIGHT": {
        const targetCellForRightDecrease = state.cells.find(
          (cell) => cell.id === activeData.cellId
        );
        const rightHandleControl = activatorEvent.target as HTMLSpanElement;

        const rightHandleBuffer = rightHandleControl.offsetWidth / 2;

        const parentActiveAreaWidthRight =
          (rightHandleControl.parentElement?.offsetWidth ?? 0) -
          rightHandleBuffer;
        const currentCellSpan = targetCellForRightDecrease?.span ?? 1;
        const sectionWidthsInParent =
          parentActiveAreaWidthRight / currentCellSpan;
        const dropPosition = parentActiveAreaWidthRight + event.delta.x;

        const rawSectionIndex = dropPosition / sectionWidthsInParent;

        // Calculate the target section based on proximity to thresholds
        // This makes all grid snaps responsive by using a rounded index instead of ceil
        const nearestSection = Math.round(rawSectionIndex);

        // Use rounded value if close to a threshold, or ceil as a fallback
        const dropSectionIndex =
          Math.abs(rawSectionIndex - nearestSection) < 0.15
            ? nearestSection
            : Math.ceil(rawSectionIndex);

        if (dropSectionIndex > currentCellSpan || dropSectionIndex < 1) {
          return;
        }

        dispatch({
          type: "CHANGE_CELL_SPAN_FROM_RIGHT",
          payload: {
            cellId: activeData.cellId,
            span: dropSectionIndex,
          },
        });
        break;
      }

      case "USER_CONTRACTS_UNIT_FROM_LEFT": {
        const targetCellForLeftDecrease = state.cells.find(
          (cell) => cell.id === activeData.cellId
        );
        const leftHandleControl = activatorEvent.target as HTMLSpanElement;

        const leftHandleBuffer = leftHandleControl.offsetWidth / 2;

        const parentActiveAreaWidthLeft =
          (leftHandleControl.parentElement?.offsetWidth ?? 0) -
          leftHandleBuffer;
        const currentCellSpanLeft = targetCellForLeftDecrease?.span ?? 1;
        const sectionWidthsInParentLeft =
          parentActiveAreaWidthLeft / currentCellSpanLeft;
        const dropPositionLeft = parentActiveAreaWidthLeft - event.delta.x;

        const rawSectionIndex = dropPositionLeft / sectionWidthsInParentLeft;

        // Calculate the target section based on proximity to thresholds
        // This makes all grid snaps responsive by using a rounded index instead of ceil
        const nearestSection = Math.round(rawSectionIndex);

        // Use rounded value if close to a threshold, or ceil as a fallback
        const dropSectionIndexLeft =
          Math.abs(rawSectionIndex - nearestSection) < 0.15
            ? nearestSection
            : Math.ceil(rawSectionIndex);

        if (
          dropSectionIndexLeft > currentCellSpanLeft ||
          dropSectionIndexLeft < 1
        ) {
          return;
        }

        const indexOffest = Math.abs(
          dropSectionIndexLeft - currentCellSpanLeft
        );
        const newStartingColumn =
          state.columns[
            state.columns.findIndex((column) => column.id === overData.column) +
              indexOffest
          ];

        if (!newStartingColumn) {
          console.warn("No new starting column found");
          return;
        }

        dispatch({
          type: "CHANGE_CELL_SPAN_FROM_LEFT",
          payload: {
            cellId: activeData.cellId,
            span: dropSectionIndexLeft,
            column: newStartingColumn.id,
          },
        });
        break;
      }

      default: {
        console.warn("No action", event);
        break;
      }
    }
  };

  const handleAddCustomUnit = (payload: Omit<CustomCellData, "id">) => {
    dispatch({
      type: "ADD_CELL",
      payload: {
        ...payload,
        span: payload.span || 1,
        isCustom: true as const,
      },
    });
  };
  const handleUpdateCustomUnit = (payload: {
    id: string;
    title: string;
    description?: string;
    row: string;
    column: string;
    span?: number;
  }) => {
    dispatch({
      type: "UPDATE_CUSTOM_CELL",
      payload: {
        ...payload,
        span: payload.span || 1,
      },
    });
  };

  const handleUndo = () => {
    dispatch({
      type: "UNDO",
    });
  };

  const handleRedo = () => {
    dispatch({
      type: "REDO",
    });
  };

  const handleRemoveUnit = (payload: { cellId: string }) => {
    dispatch({
      type: "REMOVE_CELL",
      payload: {
        cellId: payload.cellId,
      },
    });
  };

  const handleOnDeleteCellWithMissingResource = (payload: {
    cellId: string;
  }) => {
    captureAppWarning("User deleted cell with missing resource", {
      action: "delete-cell",
      area: "table",
      extra: {
        cellId: payload.cellId,
      },
    });

    dispatch({
      type: "REMOVE_CELL",
      payload: {
        cellId: payload.cellId,
      },
    });
  };

  // used in the settings modal to batch update multiple things at once
  // to avoid multiple undo steps for each individual change
  const handleBatchSettingsUpdate = (
    payload: BatchUpdateTableStructuresPayload
  ) => {
    dispatch({
      type: "BATCH_UPDATE_TABLE_STRUCTURES",
      payload: payload,
    });
  };

  const handleSaveAdaptationNote = (payload: {
    cellId: string;
    adaptationNote: string;
  }) => {
    dispatch({
      type: "UPSERT_CELL_ADAPTATION_NOTE",
      payload: {
        cellId: payload.cellId,
        adaptationNote: payload.adaptationNote,
      },
    });
  };

  return {
    handleAddCustomUnit,
    handleUndo,
    handleRedo,
    handleRemoveUnit,
    handleDragEnd,
    handleOnDeleteCellWithMissingResource,
    handleSaveAdaptationNote,
    handleBatchSettingsUpdate,
    handleUpdateCustomUnit,
    handleResetLoadingFlag: () => {
      dispatch({
        type: "RESET_LOADING_FLAG",
      });
    },
  };
};
